<template>
  <div class="flex h-full flex-col justify-between">
    <RevList
      class="divide-y-0"
      data-qa="store-nav-list-items"
      :has-external-borders="false"
    >
      <TransitionGroup
        appear
        enter-active-class="transition-all duration-150 ease-out delay-300"
        enter-from-class="opacity-0 -translate-y-2"
        @before-enter="addDelay"
      >
        <RevListItemInteractive
          v-for="(item, index) in menuItems"
          :key="item.title"
          :data-index="index"
          data-test="store-nav-list-item-mobile"
          :to="hasChildren(item) ? undefined : item.link"
          @click="emit('menuItemClick', { item })"
        >
          <template #prefix v-if="item.image">
            <RevIllustration
              alt=""
              class="h-24 w-24 transition-all"
              :height="24"
              :src="item.image"
              :width="24"
            />
          </template>
          <template #label>
            <div class="flex items-center">
              <span
                class="body-1-bold grow"
                :style="{ color: item?.style?.text_color }"
              >
                {{ item.title }}
              </span>

              <IconChevronRight v-if="hasChildren(item)" />
            </div>
          </template>
        </RevListItemInteractive>
      </TransitionGroup>
    </RevList>

    <Transition
      appear
      enter-active-class="transition-opacity duration-300 ease-out"
      enter-from-class="opacity-0"
    >
      <div
        class="flex grow flex-col justify-end pb-8"
        :style="`transition-delay: ${maxDelay}ms;`"
      >
        <RevLink
          v-for="link in links"
          :key="link.name"
          class="body-2-link flex items-center py-8 pl-20"
          :to="link.href"
          :underlined="false"
          @click="emit('linkClick', { name: link.name })"
        >
          {{ link.label }}
        </RevLink>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import type { Locale, navigationAPI } from '@backmarket/http-api'
import { RevIllustration } from '@ds/components/Illustration'
import { RevLink } from '@ds/components/Link'
import { RevList } from '@ds/components/List'
import { RevListItemInteractive } from '@ds/components/ListItemInteractive'
import { IconChevronRight } from '@ds/icons/IconChevronRight'

const props = defineProps<{
  menuItems: navigationAPI.MenuItem[]
  links: {
    name: string
    href: { name: string; params: { locale: Locale } }
    label: string
  }[]
}>()

const emit = defineEmits<{
  menuItemClick: [{ item: navigationAPI.MenuItem }]
  linkClick: [{ name: string }]
}>()

function hasChildren(item: navigationAPI.MenuItem) {
  return item.children?.length > 0
}

const maxDelay = props.menuItems.length * 50 + 300 + 150

function getIndex(el: Element) {
  // And the props are injected to first child of <li> of type
  const node = el.querySelector('*') as HTMLAnchorElement | HTMLButtonElement
  if (node.dataset.index) {
    return parseInt(node.dataset.index, 10)
  }

  return undefined
}

function addDelay(el: Element) {
  const li = el as HTMLLIElement

  const index = getIndex(el)
  if (index) {
    li.style.transitionDelay = `${index * 50 + 300}ms`
  }
}
</script>
